import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Input,
  DatePicker,
  Space,
  Empty,
  Skeleton,
  Pagination,
  Button,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { gitHistoryApi } from '../../services/gitHistoryApi';

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dates, setDates] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    gitHistoryApi
      .getCommitHistory(page, pageSize)
      .then((result) => {
        if (current) {
          setHistory(result.history);
          setTotal(result.total);
        }
      })
      .catch((failure) => {
        if (current) setError(failure.message || '变更记录暂时无法加载');
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [page, pageSize, revision]);

  const filtered = useMemo(
    () =>
      history.filter((item) => {
        const matchesText = [item.message, item.author, item.hash].some(
          (value) => value.toLowerCase().includes(search.trim().toLowerCase()),
        );
        const matchesDate =
          !dates?.[0] ||
          !dates?.[1] ||
          (dayjs(item.date).valueOf() >= dates[0].startOf('day').valueOf() &&
            dayjs(item.date).valueOf() <= dates[1].endOf('day').valueOf());
        return matchesText && matchesDate;
      }),
    [history, search, dates],
  );

  return (
    <section className="page-container">
      <header className="page-header">
        <div>
          <h1 className="page-title">开发变更记录</h1>
          <p className="page-description">
            开发提交原始记录，供排查版本变化时参考。
          </p>
        </div>
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => setRevision((value) => value + 1)}
        >
          刷新
        </Button>
      </header>
      <div className="page-content">
        <Space wrap style={{ marginBottom: 20 }}>
          <Input
            aria-label="筛选本页提交"
            placeholder="筛选本页：内容、作者或提交号"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            allowClear
            style={{ width: 280 }}
          />
          <DatePicker.RangePicker
            value={dates}
            onChange={setDates}
            placeholder={['本页起始日期', '本页结束日期']}
          />
        </Space>
        <p className="muted">
          筛选仅作用于当前页；下方分页对应全部 {total} 条提交。
        </p>
        {error ? (
          <Alert
            type="error"
            showIcon
            message={error}
            action={
              <Button onClick={() => setRevision((value) => value + 1)}>
                重试
              </Button>
            }
          />
        ) : loading ? (
          <Skeleton active={false} paragraph={{ rows: 6 }} />
        ) : filtered.length ? (
          <div className="history-list">
            {filtered.map((item) => (
              <article key={item.hash}>
                <div className="history-meta">
                  <time>
                    {item.date
                      ? dayjs(item.date).format('YYYY-MM-DD HH:mm')
                      : '日期未提供'}
                  </time>
                  <span>{item.author}</span>
                  <code>{item.hash}</code>
                </div>
                {item.message.length > 700 ? (
                  <details>
                    <summary>
                      {item.message.split('\n')[0].slice(0, 160)} · 展开完整记录
                    </summary>
                    <p>{item.message}</p>
                  </details>
                ) : (
                  <p>{item.message}</p>
                )}
              </article>
            ))}
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              search || dates
                ? '本页没有匹配的记录，可清除筛选或切换页面'
                : '暂无开发记录'
            }
          />
        )}
        <Pagination
          style={{ marginTop: 24 }}
          current={page}
          pageSize={pageSize}
          total={total}
          disabled={loading}
          showSizeChanger
          onChange={(next, size) => {
            setPage(size === pageSize ? next : 1);
            setPageSize(size);
          }}
          showTotal={(count) => '共 ' + count + ' 条'}
        />
      </div>
    </section>
  );
};
export default HistoryPage;
